/* istanbul ignore file */
import { APP_VERSION, APPLICATION_TYPE, IS_WEB, APP_ID, IS_MOBILE } from '@web/common/config'
import GTM from '@web/services/Analytics/modules/GoogleTagManager'
// import Amplitude from '@web/services/Analytics/modules/Amplitude'
import Firebase from '@web/services/Analytics/modules/Firebase'
import snakeCase from 'lodash.snakecase'
import { initClickEvent } from './utils'
import { Router } from 'vue-router'
import isEmpty from 'lodash.isempty'
import isEqual from 'lodash.isequal'

class Analytics {
  static installed = false
  private static userId?: string | null
  private static trafficType: 'external' | 'internal' = 'external'
  private static userSegments: Segment[] = []
  private static gtmId?: string
  private static superSub = false
  private static countActiveSubscriptions = 0
  private static typelk?: AnalyticsTypeLk | null
  private static courseIds?: string

  private constructor () {
    if (Analytics.installed && Analytics.gtmId != null) return
    Analytics.installed = true

    // Amplitude.init()
    if (Analytics.gtmId) {
      GTM.init({
        id: Analytics.gtmId,
        userId: Analytics.userId,
        typelk: Analytics.typelk
      })
    }
    Analytics.setAppVersion(APP_VERSION)

    initClickEvent()
  }

  static init ({ gtmId, userId, typelk }: { gtmId?: string, userId?: number, typelk?: AnalyticsTypeLk }): void {
    Analytics.gtmId = gtmId
    Analytics.userId = !userId || userId === null ? '' : String(userId)
    Analytics.typelk = typelk

    // eslint-disable-next-line no-new
    new Analytics()
  }

  private static pageData: Dictionary<string> = {}

  static setPageData (payload: Dictionary<unknown>): void {
    for (const key in payload) {
      Analytics.pageData[snakeCase(key)] = String(payload[key])
    }
  }

  static clearPageData (): void {
    Analytics.pageData = {}
  }

  static send (event: EventBase): void {
    const _event: EventBase = {
      traffic_type: Analytics.trafficType,
      user_segments: (Analytics.userSegments || []).map(segment => segment.name).join(', '),
      application_id: APP_ID,
      application_type: APPLICATION_TYPE,
      super_sub: Analytics.superSub ? 'true' : 'false',
      count_active_subscriptions: Analytics.countActiveSubscriptions.toString(),
      user_id: Analytics.userId || '',
      ...(Analytics.typelk ? { type_lk: Analytics.typelk } : {}),
      ...(Analytics.courseIds ? { course_ids: Analytics.courseIds } : {}),
      ...Analytics.pageData,
      ...event
    }

    if (IS_WEB) {
      GTM.send(_event as EventBase)
    }

    if (IS_MOBILE) {
      Firebase.send(_event)
    }

    // const {
    //   action: type,
    //   ...properties
    // } = _event
    // Amplitude.logEvent({ type, properties })
  }

  static setUserParams ({ userId, typelk }: { userId?: number | null, typelk?: AnalyticsTypeLk | null }) : void {
    const _userId = !userId || userId === null ? undefined : String(userId)
    const _typelk = !_userId || !typelk || typelk === null ? undefined : typelk
    const config: {
      user_id?: string | null
      type_lk?: AnalyticsTypeLk | null
    } = {}

    if (Analytics.userId !== _userId) {
      config.user_id = _userId
      Analytics.userId = _userId
      if (IS_MOBILE) {
        Firebase.setUserId(_userId || '')
      }
    }
    if (Analytics.typelk !== _typelk) {
      Analytics.typelk = _typelk
      config.type_lk = _typelk
    }
    if (IS_WEB && !isEmpty(config)) {
      GTM.setConfig(config)
    }
  }

  static setCourseIds (courseIds: number[]): void {
    const _courseIds = courseIds.join(',')
    if (Analytics.courseIds === _courseIds) return

    Analytics.courseIds = _courseIds
    if (IS_WEB) {
      GTM.setConfig({ course_ids: _courseIds })
    }
  }

  static setUserId (id: number | null): void {
    const _id = id === null ? '' : String(id)
    if (Analytics.userId === _id) return
    Analytics.userId = _id

    if (IS_WEB) {
      GTM.setUserId(_id)
    }

    if (IS_MOBILE) {
      Firebase.setUserId(_id)
    }

    // Amplitude.setUserId(_id)
  }

  static setTrafficType (trafficType: 'external' | 'internal'): void {
    Analytics.trafficType = trafficType
  }

  static setUserSegments (segments: Segment[]): void {
    Analytics.userSegments = segments || []
    // Amplitude.setUserProperties({ user_segments: (segments || []).map(segment => segment.name).join(', ') })
  }

  static setAppVersion (version: string): void {
    // Amplitude.setVersionName(version)
    if (IS_MOBILE) {
      Firebase.setAppVersion(version)
    }
  }

  static setSuperSub (superSub: boolean): void {
    Analytics.superSub = superSub
  }

  static setCountActiveSubscriptions (countActiveSubscriptions: number): void {
    Analytics.countActiveSubscriptions = countActiveSubscriptions
  }

  static setTypeLk (typelk?: AnalyticsTypeLk): void {
    if (Analytics.typelk === typelk) return
    Analytics.typelk = typelk
    GTM.setTypeLk(typelk)
  }

  static pageView (router: Router): void {
    if (IS_WEB) {
      GTM.pageView(router)
    }

    if (IS_MOBILE) {
      Firebase.pageView(router)
    }

    // Amplitude.pageView(router)
  }
}

export default Analytics
